import { DASHBOARD_ROUTES } from "../homeDashboard/dashboardRoutes";
import { AUTH_ROUTES } from "./authRoutes";
import React, { useState } from 'react';
import { Alert, Button, Carousel, Form } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import Swal from 'sweetalert2';
import { Shimmer } from 'react-shimmer';
import './SignIn.css';
import SigninBg from '../assets/images/signin-bg.svg';
import GoogleIcon from '../assets/images/googl-Icon.svg';
import FbIcon from '../assets/images/fb-Icon.svg';
import AppleIcon from '../assets/images/apple-Icon.svg';
import { loginUser } from '../services/authService';
import { useAuth } from '../context/AuthContext';
import { beginSocialLogin } from '../utils/socialAuth';
import { useTutorials } from '../hooks/useTutorials';
import { getFcmTokenSilently } from '../firebase/useFcmToken';

const INITIAL_FORM_STATE = {
  email: '',
  password: '',
};

const FALLBACK_TUTORIALS = [
  {
    id: 'fallback-1',
    title: 'Your Trusted Partner for Government Exam Success',
    description: 'Learn smarter, practice better, and move one step closer to your dream government job.',
    imageUrl: SigninBg,
  },
  {
    id: 'fallback-2',
    title: 'Your Trusted Partner for Government Exam Success',
    description: 'Learn smarter, practice better, and move one step closer to your dream government job.',
    imageUrl: SigninBg,
  },
  {
    id: 'fallback-3',
    title: 'Your Trusted Partner for Government Exam Success',
    description: 'Learn smarter, practice better, and move one step closer to your dream government job.',
    imageUrl: SigninBg,
  },
];

function decodeTutorialText(value) {
  if (typeof value !== 'string') return '';

  return value
    .replace(/&amp;/g, '&')
    .replace(/&nbsp;/g, ' ')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

function TutorialImage({ imageUrl }) {
  return (
    <div className="signin-tutorial-image-frame">
      <img
        src={imageUrl || SigninBg}
        alt=""
        className="signin-tutorial-image"
        onError={(event) => { event.currentTarget.src = SigninBg; }}
      />
    </div>
  );
}

function buildSessionConflictMessage(defaultMessage, sessionConflict) {
  const existingSessionLabel = sessionConflict?.existing_session_label || '';

  if (existingSessionLabel) {
    return `This account is already logged in on ${existingSessionLabel}. Do you want to continue and log out that session?`;
  }

  return defaultMessage || 'This account is already logged in on another device. Do you want to continue and reset the previous session?';
}

function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const { login } = useAuth();
  const [formValues, setFormValues] = useState(INITIAL_FORM_STATE);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeSocialProvider, setActiveSocialProvider] = useState('');
  const [shouldForceLogin, setShouldForceLogin] = useState(false);
  const [activeTutorialIndex, setActiveTutorialIndex] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const { data: tutorialData, isLoading: tutorialsLoading } = useTutorials('EN');
  const tutorials = tutorialData?.tutorials?.length ? tutorialData.tutorials : FALLBACK_TUTORIALS;

  const redirectPath = location.state?.from || DASHBOARD_ROUTES.home;

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));

    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      [name]: '',
    }));

    setSubmitError('');
  };

  const validateForm = () => {
    const nextErrors = {};
    const trimmedEmail = formValues.email.trim();

    if (!trimmedEmail) {
      nextErrors.email = 'Email is required.';
    } else if (!/\S+@\S+\.\S+/.test(trimmedEmail)) {
      nextErrors.email = 'Enter a valid email address.';
    }

    if (!formValues.password) {
      nextErrors.password = 'Password is required.';
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const completeLogin = (payload, meta) => {
    if (!payload?.access_token) {
      throw new Error(meta?.message || 'Login failed. Please try again.');
    }

    login({
      accessToken: payload.access_token,
      user: payload.user,
      expiresIn: payload.expires_in,
      tokenType: payload.token_type,
    });

    navigate(redirectPath, { replace: true });
  };

  const requestForceLoginConfirmation = async (message, sessionConflict) => {
    const result = await Swal.fire({
      title: 'Session Already Active',
      text: buildSessionConflictMessage(message, sessionConflict),
      icon: 'warning',
      iconColor: '#F2A766',
      showCancelButton: true,
      confirmButtonText: 'Continue',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      customClass: {
        popup: 'signin-force-login-popup',
        title: 'signin-force-login-title',
        htmlContainer: 'signin-force-login-text',
        actions: 'signin-force-login-actions',
        confirmButton: 'signin-force-login-confirm',
        cancelButton: 'signin-force-login-cancel',
      },
      buttonsStyling: false,
    });

    return result.isConfirmed;
  };

  const completeForceAwareLogin = async (requestPayload, { forceLogin = false } = {}) => {
    const { payload, meta } = await loginUser({
      ...requestPayload,
      force_login: forceLogin,
    });

    if ((payload?.token_message || payload?.force_login) && !forceLogin) {
      setShouldForceLogin(true);
      const shouldContinue = await requestForceLoginConfirmation(
        payload?.token_message || meta?.message,
        payload?.session_conflict || meta?.session_conflict
      );

      if (!shouldContinue) {
        setShouldForceLogin(false);
        return;
      }

      const retryResult = await loginUser({
        ...requestPayload,
        force_login: true,
      });

      setShouldForceLogin(false);
      completeLogin(retryResult.payload, retryResult.meta);
      return;
    }

    setShouldForceLogin(false);
    completeLogin(payload, meta);
  };

  const submitLogin = async ({ forceLogin = false } = {}) => {
    const deviceToken = await getFcmTokenSilently();
    await completeForceAwareLogin(
      {
        email: formValues.email.trim().toLowerCase(),
        password: formValues.password,
        login_type: 1,
        ...(deviceToken ? { device_token: deviceToken } : {}),
      },
      { forceLogin }
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      await submitLogin();
    } catch (error) {
      setShouldForceLogin(false);
      setSubmitError(error.message || 'Unable to sign in right now.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    const providerTypeMap = {
      facebook: 2,
      google: 3,
      apple: 4,
    };

    setActiveSocialProvider(provider);
    setSubmitError('');
    setShouldForceLogin(false);

    try {
      const deviceToken = await getFcmTokenSilently();
      const socialProfile = await beginSocialLogin(provider);
      await completeForceAwareLogin({
        login_type: providerTypeMap[provider],
        social_id: socialProfile.socialId,
        email: socialProfile.email || '',
        name: socialProfile.name || '',
        ...(deviceToken ? { device_token: deviceToken } : {}),
      });
    } catch (error) {
      setSubmitError(error.message || `Unable to continue with ${provider}.`);
    } finally {
      setActiveSocialProvider('');
    }
  };

  const isSocialLoading = Boolean(activeSocialProvider);
  const socialButtons = [
    { provider: 'google', icon: GoogleIcon, label: 'Continue with Google' },
    { provider: 'facebook', icon: FbIcon, label: 'Continue with Facebook' },
    { provider: 'apple', icon: AppleIcon, label: 'Continue with Apple' },
  ];

  return (
    <div className="container-fluid signin-container auth-background">
      <div className="row auth-page-row">
        <div className="col-md-6 d-flex flex-column justify-content-center align-items-center text-white left-section p-0">
          <Carousel
            controls={false}
            indicators={!tutorialsLoading && tutorials.length > 1}
            interval={4000}
            fade
            activeIndex={Math.min(activeTutorialIndex, tutorials.length - 1)}
            onSelect={(selectedIndex) => setActiveTutorialIndex(selectedIndex)}
            className="w-100 d-flex align-items-center justify-content-center"
          >
            {tutorialsLoading ? (
              <Carousel.Item>
                <div className="text-center px-4 signin-tutorial-shimmer" aria-label="Loading tutorials">
                  <Shimmer width={320} height={28} className="mx-auto mb-3" />
                  <Shimmer width={420} height={18} className="mx-auto mb-4" />
                  <Shimmer width={362} height={350} className="mx-auto" />
                </div>
              </Carousel.Item>
            ) : tutorials.map((tutorial) => (
              <Carousel.Item key={tutorial.id}>
                <div className="text-center px-4">
                  <h4 className="fw-bold ">{decodeTutorialText(tutorial.title)}</h4>
                  <p className="mb-2">{decodeTutorialText(tutorial.description)}</p>
                  <TutorialImage imageUrl={tutorial.imageUrl} />
                </div>
              </Carousel.Item>
            ))}
          </Carousel>

          {/* <div className="text-center">
            <Link to={AUTH_ROUTES.signUp}>
              <Button className="signupBtn fw-semibold px-5 py-2">Sign Up</Button>
            </Link>
          </div> */}
        </div>

        <div className="col-md-6 d-flex justify-content-center align-items-center right-section">
          <div className="signin-box auth-form-card p-4 p-md-5 rounded">
            <h3 className="fw-semibold text-center mb-2">{tutorialData?.loginTitle || 'Sign In'}</h3>
            <p className="text-center fw-medium auth-form-intro">
              By signing in to the GK cloud, users agree to comply with this sign-in policy and the app&apos;s terms of service.
            </p>

            <div className="signin-mobile d-flex d-md-none mx-auto">
              {tutorialsLoading ? <Shimmer width={272} height={263} /> : <TutorialImage imageUrl={tutorials[activeTutorialIndex]?.imageUrl} />}
            </div>

            {submitError ? (
              <Alert className="mb-3" variant={shouldForceLogin ? 'warning' : 'danger'}>
                {submitError}
              </Alert>
            ) : null}

            <Form noValidate onSubmit={handleSubmit}>
              <Form.Group className="mb-20" controlId="signInEmail">
                <Form.Label>Email</Form.Label>
                <div className="auth-form-field">
                  <span className="auth-field-icon" aria-hidden="true"><i className="bi bi-envelope" /></span>
                  <Form.Control
                    autoComplete="email"
                    isInvalid={Boolean(fieldErrors.email)}
                    name="email"
                    placeholder={isMobile ? 'Email' : 'Enter your Email'}
                    type="email"
                    value={formValues.email}
                    onChange={handleInputChange}
                  />
                </div>
                <Form.Control.Feedback type="invalid">{fieldErrors.email}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-30" controlId="signInPassword">
                <Form.Label>Password</Form.Label>
                <div className="auth-form-field auth-password-field">
                  <span className="auth-field-icon" aria-hidden="true"><i className="bi bi-lock" /></span>
                  <Form.Control
                    autoComplete="current-password"
                    isInvalid={Boolean(fieldErrors.password)}
                    name="password"
                    placeholder={isMobile ? 'Password' : 'Enter your Password'}
                    type={showPassword ? 'text' : 'password'}
                    value={formValues.password}
                    onChange={handleInputChange}
                  />
                  <button
                    className="auth-password-toggle"
                    type="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    onClick={() => setShowPassword((current) => !current)}
                  >
                    <i className={`bi ${showPassword ? 'bi-eye' : 'bi-eye-slash'}`} aria-hidden="true" />
                  </button>
                </div>
                <Form.Control.Feedback type="invalid">{fieldErrors.password}</Form.Control.Feedback>
                <div className="text-end forgot-pwd">
                  <Link to={AUTH_ROUTES.forgotPassword} className="text-decoration-none small fw-medium forgot">
                    Forgot Password?
                  </Link>
                </div>
              </Form.Group>

              <Button className="w-100 py-2 fw-semibold signin-btn" disabled={isSubmitting} type="submit">
                {isSubmitting ? 'Signing In...' : 'Sign In'}
              </Button>
            </Form>

            <div className="text-center">
              <div className="d-flex align-items-center my-20 justify-content-center connect">
                <div className="flex-grow-1 connect-border" style={{ height: '1px' }} />
                <span className="px-3 fw-medium">Or connect using</span>
                <div className="flex-grow-1 connect-border" style={{ height: '1px' }} />
              </div>

              <div className="d-flex justify-content-center gap-3 socialIcon flex-wrap">
                {socialButtons.map((socialButton) => (
                  <button
                    key={socialButton.provider}
                    className="social-login-button"
                    disabled={isSubmitting || isSocialLoading}
                    type="button"
                    onClick={() => handleSocialLogin(socialButton.provider)}
                  >
                    <img src={socialButton.icon} alt={socialButton.label} />
                    <span className="visually-hidden">
                      {activeSocialProvider === socialButton.provider ? `Connecting ${socialButton.provider}...` : socialButton.label}
                    </span>
                  </button>
                ))}
              </div>

              <p className="auth-signup-prompt">
                Don&apos;t have an account?
                <Link to={AUTH_ROUTES.signUp} className="fw-semibold text-decoration-none ms-1">
                  Sign Up
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignInPage;
